import React, { useState, useEffect, useCallback, useRef } from "react";
import { useParams } from "react-router-dom";
import {
  Card,
  Table,
  Button,
  Form,
  Input,
  Select,
  DatePicker,
  Tag,
  Spin,
  message,
  Row,
  Col,
  Modal,
  Tooltip,
  Typography,
  Drawer,
} from "antd";
import {
  DownloadOutlined,
  SearchOutlined,
  ReloadOutlined,
  LinkOutlined,
} from "@ant-design/icons";
import moment from "moment";
import Heading from "../../../../components/Heading";
import { ReportConfigService } from "../../../../services/api/reportConfig";
import { APIs } from "../../../../utils/worker";
import "../styles.scss";
import "./index.scss";

const { Text } = Typography;
const { Option } = Select;
const { RangePicker } = DatePicker;

// ── Cell type renderer ────────────────────────────────────────────────────────

function renderCell(value, colDef, row, onDrilldown) {
  if (value === null || value === undefined) return <Text type="secondary">—</Text>;

  switch (colDef.cellType) {
  case "date":
    return value ? moment(value).format("DD-MM-YYYY HH:mm") : "—";

  case "number":
    return (
      <span style={{ fontVariantNumeric: "tabular-nums" }}>
        {Number(value).toLocaleString("en-IN")}
      </span>
    );

  case "badge": {
    const colorMap = colDef.badgeColors || {};
    const color = colorMap[value];
    if (color) {
      return (
        <span
          style={{
            background: `${color}22`,
            color,
            border: `1px solid ${color}55`,
            borderRadius: 12,
            padding: "2px 10px",
            fontSize: 12,
            fontWeight: 600,
            whiteSpace: "nowrap",
          }}
        >
          {value}
        </span>
      );
    }
    return <Tag>{value}</Tag>;
  }

  case "link": {
    const href = (colDef.linkTemplate || "").replace(
      /\{(\w+)\}/g,
      (_, k) => row[k] ?? ""
    );
    return (
      <a href={href} target={colDef.linkTarget || "_blank"} rel="noreferrer">
        {value} <LinkOutlined style={{ fontSize: 11 }} />
      </a>
    );
  }

  case "clickable_count":
  case "clickable_text":
    return (
      <span
        style={{ color: "#4263eb", cursor: "pointer", textDecoration: "underline", fontWeight: colDef.cellType === "clickable_count" ? 600 : 400 }}
        onClick={() => onDrilldown && onDrilldown(colDef, row)}
      >
        {colDef.cellType === "clickable_count" ? Number(value).toLocaleString("en-IN") : value}
      </span>
    );

  // eslint-disable-next-line no-fallthrough
  default: {
    // Auto-format any backend date/datetime string, including full ISO 8601:
    // "2026-02-18T10:59:26.233+00:00", "2025-03-22 14:30:00", "2025-03-22"
    if (typeof value === "string" && /^\d{4}-\d{2}-\d{2}/.test(value)) {
      const m = moment(value); // non-strict: handles ISO 8601 + tz offsets + ms
      if (m.isValid()) {
        return value.length > 10
          ? m.format("YYYY-MM-DD HH:mm")
          : m.format("YYYY-MM-DD");
      }
    }
    return value;
  }
  }
}

// ── Resolve param value ───────────────────────────────────────────────────────

function resolveParamValue(template, row, filterValues) {
  if (!template) return undefined;
  // {row.colKey}
  if (template.startsWith("{row.") && template.endsWith("}")) {
    const key = template.slice(5, -1);
    return row[key];
  }
  // __filter.paramKey
  if (template.startsWith("__filter.")) {
    const key = template.slice(9);
    return filterValues[key];
  }
  // plain string
  return template;
}

// ── Filter control renderer ───────────────────────────────────────────────────

function FilterControl({ filterDef }) {
  const { key, type, label, placeholder, options, dropdownApiUrl, labelField, valueField } = filterDef;
  const [apiOptions, setApiOptions] = useState([]);

  useEffect(() => {
    // Clean type: strip trailing labels/spaces if present (e.g., "type — label" -> "type")
    const cleanType = (type || "").split(/[^a-zA-Z0-9_]/)[0].toLowerCase();

    const isDynamic = (
      cleanType === "dynamic_select" ||
      cleanType === "dynamic_multi_select" ||
      cleanType === "dynamic_multi_select_string" ||
      cleanType === "dynamic_multi_select_int"
    );

    if (isDynamic && dropdownApiUrl) {
      // Build full URL: if it's already absolute use as-is,
      // otherwise prepend reportURL (same pattern used throughout the project).
      const reportURL = APIs.reportURL || "";
      const fullUrl = /^https?:\/\//i.test(dropdownApiUrl)
        ? dropdownApiUrl
        : `${reportURL}${dropdownApiUrl}`;

      const token = localStorage.getItem("userinfo")
        ? `Bearer ${JSON.parse(localStorage.getItem("userinfo"))?.accessToken}`
        : null;

      fetch(fullUrl, {
        headers: token ? { Authorization: token } : {},
      })
        .then((r) => r.json())
        .then((data) => {
          const raw = Array.isArray(data) ? data : data.data || [];
          setApiOptions(raw.map((o) => {
            if (typeof o === "string") return { label: o, value: o };
            return {
              label: o[labelField || "label"] ?? o["name"] ?? o["label"] ?? String(o),
              value: o[valueField || "value"] ?? o["id"]   ?? o["value"] ?? String(o),
            };
          }));
        })
        .catch(() => {});
    }
  }, [type, dropdownApiUrl, labelField, valueField]);

  const colSpan = filterDef.colSpan || 3;

  const control = (() => {
    // Clean type for rendering switch as well
    const cleanType = (type || "").split(/[^a-zA-Z0-9_]/)[0].toLowerCase();

    switch (cleanType) {
    case "date":
    case "date_from":
      return (
        <DatePicker
          style={{ width: "100%" }}
          placeholder={placeholder || `Select ${label}`}
          format="YYYY-MM-DD"
        />
      );
    case "date_to":
      return (
        <DatePicker
          style={{ width: "100%" }}
          placeholder={placeholder || `Select ${label}`}
          format="YYYY-MM-DD"
        />
      );
    case "date_range":
      return (
        <RangePicker
          style={{ width: "100%" }}
          placeholder={[`${label} From`, `${label} To`]}
          format="YYYY-MM-DD"
        />
      );
    case "select":
      return (
        <Select
          allowClear
          showSearch
          placeholder={placeholder || `Select ${label}`}
          style={{ width: "100%" }}
        >
          {(options || []).map((o) => (
            <Option key={o} value={o}>{o}</Option>
          ))}
        </Select>
      );
    case "multi_select":
      return (
        <Select
          mode="multiple"
          allowClear
          showSearch
          maxTagCount="responsive"
          placeholder={placeholder || `Select ${label}`}
          style={{ width: "100%" }}
        >
          {(options || []).map((o) => (
            <Option key={o} value={o}>{o}</Option>
          ))}
        </Select>
      );
    case "dynamic_select":
      return (
        <Select
          allowClear
          showSearch
          placeholder={placeholder || `Select ${label}`}
          style={{ width: "100%" }}
          optionFilterProp="label"
        >
          {apiOptions.map((o) => (
            <Option key={o.value} value={o.value} label={o.label}>{o.label}</Option>
          ))}
        </Select>
      );
    case "dynamic_multi_select":
    case "dynamic_multi_select_string":
    case "dynamic_multi_select_int":
      return (
        <Select
          mode="multiple"
          allowClear
          showSearch
          maxTagCount="responsive"
          placeholder={placeholder || `Select ${label}`}
          style={{ width: "100%" }}
          optionFilterProp="label"
        >
          {apiOptions.map((o) => (
            <Option key={o.value} value={String(o.value)} label={o.label}>{o.label}</Option>
          ))}
        </Select>
      );
    case "integer":
    case "number":
      return <Input type="number" placeholder={placeholder || label} />;
    default:
      return <Input placeholder={placeholder || label} allowClear />;
    }
  })();

  return (
    <Col xs={24} sm={12} md={8} lg={colSpan} key={key}>
      <div className="filter-label">{label || key}</div>
      <Form.Item name={key} style={{ marginBottom: 0 }}>
        {control}
      </Form.Item>
    </Col>
  );
}

// ── Build query params from form values and filter definitions ────────────────

function buildQueryParams(values, filterDefs) {
  const params = {};
  (filterDefs || []).forEach((fd) => {
    const val = values[fd.key];
    if (val === undefined || val === null || val === "") return;

    if (fd.type === "date" || fd.type === "date_from" || fd.type === "date_to") {
      params[fd.key] = moment.isMoment(val) ? val.format("YYYY-MM-DD") : val;
    } else if (fd.type === "date_range" && Array.isArray(val)) {
      params[`${fd.key}From`] = val[0]?.format("YYYY-MM-DD");
      params[`${fd.key}To`] = val[1]?.format("YYYY-MM-DD");
    } else if (Array.isArray(val) || fd.type === "dynamic_multi_select" || fd.type === "dynamic_multi_select_string" || fd.type === "dynamic_multi_select_int") {
      params[fd.key] = Array.isArray(val) ? val.join(",") : val;
    } else {
      params[fd.key] = val;
    }
  });
  return params;
}

// ── Main DynamicReport Component ─────────────────────────────────────────────

const DynamicReport = ({ reportKey: propReportKey, prefillParams, isModal = false }) => {
  const { reportKey: routeReportKey } = useParams();
  const effectiveKey = propReportKey || routeReportKey;

  const [schema, setSchema] = useState(null);
  const [schemaLoading, setSchemaLoading] = useState(true);
  const [tableData, setTableData] = useState([]);
  const [dataLoading, setDataLoading] = useState(false);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 20,
    total: 0,
    showSizeChanger: true,
    showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`,
  });

  // Drill-down state
  const [drillOpen, setDrillOpen] = useState(false);
  const [drillReportKey, setDrillReportKey] = useState(null);
  const [drillPrefill, setDrillPrefill] = useState({});
  const [drillTitle, setDrillTitle] = useState("");
  const [drillMode, setDrillMode] = useState("modal");

  const [form] = Form.useForm();
  const filterValuesRef = useRef({});
  const prefillRef     = useRef({});  // stores raw drill-down params for every API call

  // ── Load schema ─────────────────────────────────────────────────────────

  useEffect(() => {
    if (!effectiveKey) return;
    setSchemaLoading(true);
    ReportConfigService.getUiSchema(effectiveKey)
      .then((res) => {
        const s = res.data || res;
        setSchema(s);
        // set default page size from schema
        if (s.pageConfig?.defaultPageSize) {
          setPagination((p) => ({ ...p, pageSize: s.pageConfig.defaultPageSize }));
        }

        const fieldValues = {};

        if (prefillParams && Object.keys(prefillParams).length) {
          // Drill-down mode: store raw params so they always reach the API
          prefillRef.current = prefillParams;

          // Also pre-fill any filter fields that match
          (s.filters || []).forEach((fd) => {
            if (prefillParams[fd.key] !== undefined) {
              fieldValues[fd.key] = (fd.type === "date" || fd.type === "date_from" || fd.type === "date_to")
                ? moment(prefillParams[fd.key])
                : prefillParams[fd.key];
            }
          });
        } else {
          prefillRef.current = {};
          // Normal open: default all date filters to yesterday → today
          const today = moment();
          const yesterday = moment().subtract(1, "days");
          (s.filters || []).forEach((fd) => {
            if (fd.type === "date" || fd.type === "date_from") {
              fieldValues[fd.key] = yesterday;
            } else if (fd.type === "date_to") {
              fieldValues[fd.key] = today;
            } else if (fd.type === "date_range") {
              fieldValues[fd.key] = [yesterday, today];
            }
          });
        }

        if (Object.keys(fieldValues).length) {
          form.setFieldsValue(fieldValues);
        }
      })
      .catch(() => message.error("Failed to load report schema"))
      .finally(() => setSchemaLoading(false));
  }, [effectiveKey]); // eslint-disable-line

  // ── Auto-fetch when schema is ready & requiresFilter=false ───────────────

  useEffect(() => {
    if (!schema) return;
    const requiresFilter = schema.pageConfig?.requiresFilter;
    if (!requiresFilter) {
      fetchData(1, pagination.pageSize, {});
    }
  }, [schema]); // eslint-disable-line

  // ── Fetch data ───────────────────────────────────────────────────────────

  const fetchData = useCallback(async (page = 1, pageSize = 20, extraParams = {}) => {
    if (!effectiveKey) return;
    setDataLoading(true);
    try {
      const formValues = form.getFieldsValue();
      filterValuesRef.current = formValues;
      const queryParams = {
        // 1. Raw drill-down params first (e.g. LCRId for external APIs)
        ...prefillRef.current,
        // 2. Form filter values override (standard filter-based params)
        ...buildQueryParams(formValues, schema?.filters || []),
        // 3. Explicit extra params override everything
        ...extraParams,
        page,
        pageSize,
      };

      // Use custom dataUrl from pageConfig if configured, else use generic engine
      const customDataUrl = schema?.pageConfig?.dataUrl;
      let result;
      if (customDataUrl) {
        result = await ReportConfigService.getDataFromUrl(customDataUrl, queryParams);
      } else {
        result = await ReportConfigService.getData(effectiveKey, queryParams);
      }

      // Normalise response: external APIs may return a flat array or a
      // different shape than the internal engine's {data, pagination} wrapper
      let rows = [];
      let pag  = {};
      if (Array.isArray(result)) {
        // External API returned a raw array directly
        rows = result;
        pag  = { currentPage: 0, pageSize: rows.length, totalRecords: rows.length };
      } else {
        rows = result.data || [];
        pag  = result.pagination || {};
      }

      setTableData(rows);
      setPagination((prev) => ({
        ...prev,
        current: (pag.currentPage ?? page - 1) + 1,
        pageSize: pag.pageSize ?? pageSize,
        total: pag.totalRecords ?? pag.totalElements ?? rows.length ?? 0,
      }));
    } catch (err) {
      message.error("Failed to load report data: " + (err.message || "Unknown error"));
      setTableData([]);
    } finally {
      setDataLoading(false);
    }
  }, [effectiveKey, schema, form]);

  // ── Search / Reset ───────────────────────────────────────────────────────

  const handleSearch = () => fetchData(1, pagination.pageSize);
  const handleReset = () => {
    const filters = schema?.filters || [];
    const today     = moment();
    const yesterday = moment().subtract(1, "days");

    // Build the new field values after reset:
    // - Date filters  → restored to yesterday/today defaults (never blank)
    // - Required filters → restored to their configured defaultValue (not blanked)
    // - Everything else → cleared (undefined)
    const resetValues = {};
    filters.forEach((fd) => {
      const cleanType = (fd.type || "").split(/[^a-zA-Z0-9_]/)[0].toLowerCase();
      if (cleanType === "date" || cleanType === "date_from") {
        // Always restore date-from to yesterday
        resetValues[fd.key] = yesterday;
      } else if (cleanType === "date_to") {
        // Always restore date-to to today
        resetValues[fd.key] = today;
      } else if (cleanType === "date_range") {
        // Always restore range to [yesterday, today]
        resetValues[fd.key] = [yesterday, today];
      } else if (fd.required && fd.defaultValue) {
        // Required field with a configured default → restore default, don't blank
        resetValues[fd.key] = fd.defaultValue;
      } else if (fd.required) {
        // Required but no defaultValue → keep existing value (don't wipe a required field)
        resetValues[fd.key] = form.getFieldValue(fd.key);
      } else {
        // Optional non-date filter → clear it
        resetValues[fd.key] = undefined;
      }
    });

    form.setFieldsValue(resetValues);
    fetchData(1, pagination.pageSize);
  };
  const handleTableChange = (pag) => fetchData(pag.current, pag.pageSize);

  // ── Download ─────────────────────────────────────────────────────────────

  const handleDownload = async () => {
    try {
      const formValues = form.getFieldsValue();
      const queryParams = buildQueryParams(formValues, schema?.filters || []);
      const customDownloadUrl = schema?.pageConfig?.downloadUrl;
      if (customDownloadUrl) {
        await ReportConfigService.downloadFromUrl(
          customDownloadUrl,
          queryParams,
          `${effectiveKey}-report.csv`
        );
      } else {
        await ReportConfigService.download(effectiveKey, queryParams);
      }
      message.success("Download started!");
    } catch (err) {
      message.error("Download failed: " + (err.message || "Unknown error"));
    }
  };

  // ── Drill-down handler ───────────────────────────────────────────────────

  const handleDrilldown = (colDef, row) => {
    const resolvedParams = {};
    Object.entries(colDef.clickParamMap || {}).forEach(([paramKey, template]) => {
      const val = resolveParamValue(template, row, filterValuesRef.current);
      if (val !== undefined) resolvedParams[paramKey] = val;
    });

    // Resolve modal title placeholders like "Pending at {row.hub_name}"
    const title = (colDef.clickModalTitle || colDef.clickReportKey || "Details").replace(
      /\{row\.(\w+)\}/g,
      (_, k) => row[k] ?? ""
    );

    setDrillReportKey(colDef.clickReportKey);
    setDrillPrefill(resolvedParams);
    setDrillTitle(title);
    setDrillMode(colDef.clickOpenMode || "modal");
    setDrillOpen(true);
  };

  // ── Build table columns from schema ─────────────────────────────────────

  const buildColumns = () => {
    if (!schema?.columns?.length) return [];
    // Helper: snake_case / camelCase → Title Case
    const toTitle = (key) =>
      key
        .replace(/_/g, " ")
        .replace(/([a-z])([A-Z])/g, "$1 $2")
        .replace(/\b\w/g, (l) => l.toUpperCase());

    return schema.columns
      .filter((c) => !c.hidden)
      .sort((a, b) => (a.order ?? 999) - (b.order ?? 999))
      .map((c) => ({
        title: c.header || toTitle(c.key),
        dataIndex: c.key,
        key: c.key,
        sorter: c.sortable ?? true,
        // min 150px so columns never collapse into each other
        width: c.width ? parseInt(c.width) : 150,
        render: (val, row) => renderCell(val, c, row, handleDrilldown),
        align: c.cellType === "number" || c.cellType === "clickable_count" ? "right" : "left",
        ellipsis: !c.wrap,
      }));
  };

  // ── Loading / Error state ────────────────────────────────────────────────

  if (schemaLoading) {
    return (
      <div className="dr-loading">
        <Spin size="large" tip={`Loading ${effectiveKey}...`} />
      </div>
    );
  }

  if (!schema) {
    return (
      <div className="dr-error">
        <Text type="danger">Report schema not found for key: {effectiveKey}</Text>
      </div>
    );
  }

  const pc = schema.pageConfig || {};
  const filters = schema.filters || [];
  const pageTitle = schema.displayName || effectiveKey;
  const pageIcon = pc.icon || "📋";
  const isDownloadable = pc.downloadable ?? true;
  const showSerialNumber = pc.showSerialNumber ?? true;

  const columns = [
    ...(showSerialNumber
      ? [{
        title: "#",
        key: "_sno",
        width: 55,
        render: (_, __, idx) => (pagination.current - 1) * pagination.pageSize + idx + 1,
      }]
      : []),
    ...buildColumns(),
  ];

  const pageContent = (
    <div className={`report-container dr-dynamic-report ${isModal ? "dr-modal-mode" : ""}`}>
      {/* Header */}
      {!isModal && (
        <div style={{ backgroundColor: "#fff", padding: "8px 16px", borderRadius: 8, marginBottom: 12 }}>
          <Heading title={`${pageIcon} ${pageTitle}`} navigateTo="/reports">
            {isDownloadable && (
              <Button
                type="primary"
                icon={<DownloadOutlined />}
                onClick={handleDownload}
                loading={dataLoading}
                style={{ marginLeft: "auto" }}
              >
                Download CSV
              </Button>
            )}
          </Heading>
        </div>
      )}

      {/* Filter bar */}
      {filters.length > 0 && (
        <Card
          size="small"
          className="report-card"
          bodyStyle={{ paddingBottom: 4 }}
          style={{ marginBottom: 12 }}
        >
          <Form form={form} layout="vertical">
            <Row gutter={[12, 0]} className="filters-section" align="bottom">
              {filters
                .filter((f) => !f.hidden)
                .sort((a, b) => (a.order ?? 99) - (b.order ?? 99))
                .map((fd) => (
                  <FilterControl key={fd.key} filterDef={fd} formInstance={form} />
                ))}

              {/* Action buttons */}
              <Col xs={24} sm={12} md={8} lg={3}>
                <div className="filter-label">&nbsp;</div>
                <Button
                  type="primary"
                  icon={<SearchOutlined />}
                  onClick={handleSearch}
                  loading={dataLoading}
                  style={{ width: "100%", height: 32 }}
                >
                  Search
                </Button>
              </Col>
              <Col xs={24} sm={12} md={4} lg={2}>
                <div className="filter-label">&nbsp;</div>
                <Tooltip title="Reset all filters">
                  <Button
                    icon={<ReloadOutlined />}
                    onClick={handleReset}
                    style={{ width: "100%", height: 32 }}
                  />
                </Tooltip>
              </Col>
              {isModal && isDownloadable && (
                <Col xs={24} sm={12} md={4} lg={3}>
                  <div className="filter-label">&nbsp;</div>
                  <Button
                    icon={<DownloadOutlined />}
                    onClick={handleDownload}
                    style={{ width: "100%", height: 32 }}
                  >
                    CSV
                  </Button>
                </Col>
              )}
            </Row>
          </Form>
        </Card>
      )}

      {/* Table */}
      <Spin spinning={dataLoading}>
        <Table
          rowKey={(row, idx) => row.id ?? row._id ?? idx}
          columns={columns}
          dataSource={tableData}
          loading={false}
          pagination={pagination}
          onChange={handleTableChange}
          scroll={{ x: "max-content", y: isModal ? "45vh" : "calc(100vh - 340px)" }}
          size="middle"
          sticky
        />
      </Spin>

      {/* ── Drill-down Modal / Drawer ── */}
      {drillMode === "drawer" ? (
        <Drawer
          title={drillTitle}
          open={drillOpen}
          onClose={() => setDrillOpen(false)}
          width="70vw"
          extra={
            isDownloadable && (
              <Button size="small" icon={<DownloadOutlined />}>
                CSV
              </Button>
            )
          }
        >
          {drillOpen && drillReportKey && (
            <DynamicReport
              reportKey={drillReportKey}
              prefillParams={drillPrefill}
              isModal
              onClose={() => setDrillOpen(false)}
            />
          )}
        </Drawer>
      ) : (
        <Modal
          title={drillTitle}
          open={drillOpen}
          onCancel={() => setDrillOpen(false)}
          width="80vw"
          footer={null}
          destroyOnClose
        >
          {drillOpen && drillReportKey && (
            <DynamicReport
              reportKey={drillReportKey}
              prefillParams={drillPrefill}
              isModal
              onClose={() => setDrillOpen(false)}
            />
          )}
        </Modal>
      )}
    </div>
  );

  return pageContent;
};

export default DynamicReport;
